/**
 * Socket handler registry for SMS.
 *
 * Adding a new event type = adding a module here, NOT patching hook files.
 * The registry is shared between live WS frames (via `SocketMessageRouter`)
 * and snapshot replay on reconnect (via `snapshotDispatcher`), so decode
 * mappings live in exactly one place.
 */

import { createAlertHandlers } from './alertHandlers';
import { createReunificationHandlers } from './reunificationHandlers';
import type { HandlerDeps, SocketHandlerMap } from './types';

export type { HandlerDeps, SocketHandler, SocketHandlerMap } from './types';
export { REUNIFICATION_EVENT_TYPES } from './reunificationHandlers';
export { ALERT_EVENT_TYPES } from './alertHandlers';
export { dispatchSnapshot } from './snapshotDispatcher';

export function createSocketHandlers(deps: HandlerDeps): SocketHandlerMap {
  return {
    ...createReunificationHandlers(deps),
    ...createAlertHandlers(deps),
  };
}

/**
 * Parse a raw socket text frame into `{ type, data, raw }` or null.
 *
 * The backend wraps the payload as
 *   { type: 'reunification.event.activated', event_id, timestamp, data: { ... } }
 * and a few code paths double-encode `data` as a JSON string. Both shapes
 * are handled here.
 *
 * No minimum-length gate: the shortest valid reunification frame
 * (`{"type":"reunification.activated"}`) is well over any such bound, and
 * artificial length thresholds just hide malformed-frame bugs.
 */
export function parseSocketMessage(raw: string): {
  type: string;
  data: Record<string, unknown>;
  raw: Record<string, unknown>;
} | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const type = typeof parsed['type'] === 'string' ? (parsed['type'] as string) : '';
    if (!type) return null;

    let data: Record<string, unknown> = {};
    const rawData = parsed['data'];
    if (typeof rawData === 'string') {
      try {
        data = JSON.parse(rawData) as Record<string, unknown>;
      } catch {
        data = {};
      }
    } else if (rawData && typeof rawData === 'object') {
      data = rawData as Record<string, unknown>;
    }

    return { type, data, raw: parsed };
  } catch {
    return null;
  }
}
